import { Accordion, Center, Text, Container, useMantineTheme, CopyButton, Tooltip, ActionIcon, rem, Badge, Loader, Indicator, Divider, Title, Progress, Stack, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classes from './OrderList.module.css';
import { useSession } from "../../auth/SessionContext";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { format } from 'date-fns';
import { DEBUG } from "../..";
import { AppRefData, RefKeyMonthly, LoadRefKeyDataAsync, RefKeys } from './RefReport';
import { Features, hasFlag, listApps, allFeatures, packageEnd, parseApiDate } from "../../scripts/FeatureCheck";

export interface OrderData {
  id: number;
  ident: string;
  owner: string;
  discordId: string | null;
  created: string;
  features: number;
  refunded: boolean;
  steamId: number;
  strSteamId: string;
  steamIdBindingTime: string;
  affiliate: string;
  validUntil?: string | null;
  // Paid, time-limited access lives in a Feature-Grant, not on the key: a Welcome
  // Package key has features 0 and no validUntil of its own. packageValidUntil is the
  // backend's convenience field for the latest package end (null when there is none).
  packageValidUntil?: string | null;
  featureGrants?: { features: number; validUntil: string; type?: string }[];
}

export async function LoadOrderDataAsync(): Promise<OrderData[]>{
  return fetch('dyn/get-orders.php', {
    method: 'GET',
    credentials: 'include' // Dies sendet die Cookies mit
  })
  .then(response => response.json())
  .catch(error => { if(DEBUG) console.error('Error:', error) });
}
function formatDate(dateString : string): string {
  try {
    const date = parseApiDate(dateString);
    return format(date, 'dd.MM.yyyy HH:mm');
  } catch {
    return dateString;
  }
}

function getTimeRemaining(validUntil: string): { days: number; hours: number; totalHours: number; expired: boolean } {
  try {
    const expiry = parseApiDate(validUntil);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    if (diffMs <= 0) return { days: 0, hours: 0, totalHours: 0, expired: true };
    const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    return { days, hours, totalHours, expired: false };
  } catch {
    return { days: 0, hours: 0, totalHours: 0, expired: true };
  }
}

function ValidUntilCountdown({ validUntil }: { validUntil: string }) {
  const { t } = useTranslation("translation");
  const { days, hours, expired } = getTimeRemaining(validUntil);
  const totalDays = 90;
  const daysUsed = totalDays - days;
  const progressValue = Math.max(0, Math.min(100, (daysUsed / totalDays) * 100));
  const progressColor = days > 30 ? "green" : days > 7 ? "orange" : "red";

  if (expired) {
    return (
      <Badge color="red" variant="filled" size="sm">
        {t("orders.countdown.expired")}
      </Badge>
    );
  }

  return (
    <Stack gap={4}>
      <Group gap={6} align="center">
        <Badge color={progressColor} variant="light" size="sm">
          {t("orders.countdown.temporary")}
        </Badge>
        <Text size="xs" fw={600} c={progressColor}>
          {days}d {hours}h {t("orders.countdown.remaining")}
        </Text>
      </Group>
      <Progress value={progressValue} color={progressColor} size="xs" radius="xl" style={{ maxWidth: 200 }} />
      <Text size="xs" c="dimmed">
        {t("orders.countdown.validUntil")}: {formatDate(validUntil)}
      </Text>
    </Stack>
  );
}
const monthNames = [
  '', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

interface MonthlySummary {
  count: number;
  refunds: number;
  revenue: number;
}


export function OrderList() {
  const navigate = useNavigate();

  const { t } = useTranslation("translation");
  const handleRedirect = () => {
    navigate('/');
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);  
  const [orderData, setOrderData] = useState<OrderData[]>([]);
  const [refData, setRefData] = useState<AppRefData>({});
  const { sessionData, loadSessionDataAsync, deleteSessionAsync, setSessionData } = useSession();
  const theme = useMantineTheme();
  useEffect(() => {
    const fetchData = async () => {
      try {
        var result = await loadSessionDataAsync();
        if(DEBUG) console.log("Imported sessionData in OrderList: ", result);
        if(!result) {
          handleRedirect();
        } else {
          const orderData = await LoadOrderDataAsync();
          LoadRefKeyDataAsync().then(setRefData);

          if(DEBUG) console.log("Order Data: ", orderData);
          // allFeatures, not od.features: a Welcome Package key carries features 0 and
          // holds its apps in a grant, so filtering on the base mask hides it entirely.
          const acadOrderData = orderData.filter(od => hasFlag(allFeatures(od), Features.DRIVE));
          if(DEBUG) console.log("Order Data: ", acadOrderData);
          setOrderData(acadOrderData);
          setLoading(false);
        }
      } catch (err) {
        if (err instanceof Error) {
          if(DEBUG) console.error('Error:', err.message);
          setError(err.message);
        } else {
          if(DEBUG) console.error('Unknown error:', err);
          setError('An unknown error occurred');
        }
        setLoading(false);
      }
    };
    
    if(process.env.NODE_ENV !== 'development')
      fetchData();
    else{
      setOrderData([]);
      setLoading(false);
    }
  }, []);



  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <div>
          <Loader color="var(--mantine-color-yellow-filled)" size="xl" />
          <Text size="xl" mt="md">Loading...</Text>
        </div>
      </Center>
    );
  }
  const groupByYearMonth = (): Record<number, Record<number, { appName: string; data: RefKeyMonthly }[]>> => {
    const grouped: Record<number, Record<number, { appName: string; data: RefKeyMonthly }[]>> = {};

    for (const [appName, entries] of Object.entries(refData)) {
      for (const entry of entries) {
        const { year, month } = entry;
        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = [];
        grouped[year][month].push({ appName, data: entry });
      }
    }
    return grouped;
  };
  const grouped = groupByYearMonth();
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Container size="100%" style={{ maxWidth: theme.breakpoints.lg, width: '100%' }}>
      <Accordion multiple variant="separated" defaultValue={['item-0']}>
        <Accordion.Item key={`orderList`} value={`orderList`}>
          <Accordion.Control>
            Your Keys
          </Accordion.Control>
          <Accordion.Panel>
            <Accordion multiple variant="separated" defaultValue={['item-0']}>
              {orderData.map((data, index) => {
                // A Welcome Package key has no validUntil of its own — its end sits in the
                // Feature-Grant, so the countdown has to read both.
                const expiresAt = data.validUntil ?? packageEnd(data)?.toISOString() ?? null;
                const isTemporary = !!expiresAt;
                const timeLeft = expiresAt ? getTimeRemaining(expiresAt) : null;
                const headerColor = isTemporary
                  ? (timeLeft?.expired ? 'red' : timeLeft && timeLeft.days <= 7 ? 'red' : timeLeft && timeLeft.days <= 30 ? 'orange' : 'green')
                  : undefined;
                return (
                  <Accordion.Item key={`key-${index}`} value={`item-${index}`}>
                    <Accordion.Control className={data.refunded ? classes.refunded : ''}>
                      <Group gap="sm" align="center">
                        <Indicator inline color="gray" className={classes.badge} label={t("orders.header.creationDate")} position="middle-end" size={16}>
                          {formatDate(data.created)}
                        </Indicator>
                        {isTemporary && (
                          <Badge color={headerColor} variant="dot" size="sm">
                            {timeLeft?.expired
                              ? t("orders.countdown.expired")
                              : `${timeLeft?.days}d ${timeLeft?.hours}h`}
                          </Badge>
                        )}
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel className={data.refunded ? classes.refunded : ''}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 0.9fr', gap: '8px', alignItems: 'baseline', alignContent: 'stretch', gridAutoRows: 'minmax(auto, max-content)' }}>
                        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.owner")}:</strong></div>
                        <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.owner}</div>
                        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.key")}:</strong></div>
                        <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.ident}
                          <CopyButton value={data.ident} timeout={2000}>
                            {({ copied, copy }) => (
                              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                                <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                                  {copied ? (
                                    <IconCheck style={{ width: rem(16) }} />
                                  ) : (
                                    <IconCopy style={{ width: rem(16) }} />
                                  )}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.activatedTo")}:</strong></div>
                        <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.strSteamId}</div>
                        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.afiliate")}:</strong></div>
                        <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.affiliate}</div>
                      </div>
                      {expiresAt && (
                        <>
                          <Divider my="sm" variant="dashed" />
                          <ValidUntilCountdown validUntil={expiresAt} />
                        </>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Accordion.Panel>
        </Accordion.Item>
        
        <Accordion.Item key={`acciliateReport`} value={`affiliateReport`}>
          <Accordion.Control>
            Affiliate Report
          </Accordion.Control>
          <Accordion.Panel>
            <Accordion multiple variant="separated">
              {Object.entries(grouped).sort(([a], [b]) => +b - +a).map(([year, months]) => {
                const yearSummary = Object.values(months).flat().reduce((acc, { data }) => {
                  data.refKeys.forEach(rk => {
                    acc.count += rk.count;
                    acc.refunds += rk.refunds;
                    acc.revenue += rk.revenue * rk.share;
                  });
                  return acc;
                }, { count: 0, refunds: 0, revenue: 0 });

                return (
                  <Accordion.Item key={`year-${year}`} value={`year-${year}`}>
                    <Accordion.Control>
                      Jahr {year} – {yearSummary.revenue.toFixed(2)} € - {yearSummary.count} Keys
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Accordion multiple variant="separated">
                        {Object.entries(months).sort(([a], [b]) => +b - +a).map(([month, entries]) => {
                          const monthSummary = entries.flatMap(e => e.data.refKeys).reduce((acc, rk) => {
                            acc.count += rk.count;
                            acc.refunds += rk.refunds;
                            acc.revenue += rk.revenue * rk.share;
                            return acc;
                          }, { count: 0, refunds: 0, revenue: 0 });

                          return (
                            <Accordion.Item key={`month-${year}-${month}`} value={`month-${year}-${month}`}>
                              <Accordion.Control>
                                {monthNames[+month]} {year} – {monthSummary.revenue.toFixed(2)} € - {monthSummary.count} Keys
                              </Accordion.Control>
                              <Accordion.Panel>
                                <div>
                                  {Object.entries(
                                    entries.reduce((acc, { appName, data }) => {
                                      if (!acc[appName]) acc[appName] = [];
                                      acc[appName].push(...data.refKeys);
                                      return acc;
                                    }, {} as Record<string, RefKeys[]>)
                                  ).map(([appName, refKeys]) => {
                                    const total = refKeys.reduce((acc, rk) => {
                                      acc.count += rk.count;
                                      acc.refunds += rk.refunds;
                                      acc.revenue += rk.revenue * rk.share;
                                      return acc;
                                    }, { count: 0, refunds: 0, revenue: 0 });

                                    return (
                                      <div key={appName} style={{ marginBottom: 12, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
                                        <strong>{appName}</strong><br />
                                        Keys: {total.count}, Refunds: {total.refunds}, Revenue: {total.revenue.toFixed(2)} €

                                        <div style={{ marginTop: 6, marginLeft: 12 }}>
                                          {refKeys.map((rk, i) => (
                                            <div key={i} style={{ fontSize: '0.9em', padding: '2px 0' }}>
                                              <strong>{rk.refName}</strong>: {rk.count} Keys, {rk.refunds} Refunds, {(rk.revenue * rk.share).toFixed(2)} €
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </Accordion.Panel>
                            </Accordion.Item>
                          );
                        })}
                      </Accordion>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Container>
  );
}
